import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import RestaurantStoryAnimation from './RestaurantStoryAnimation';
// Styles provided by RestaurantOnboarding.css

/* ─── Data ─────────────────────────────────────────────────── */
const TERMS_SECTIONS = [
  {
    title: '1. Identificación de la empresa',
    body: 'Jama es una plataforma tecnológica operada por Beez Cía. Ltda., compañía legalmente constituida bajo las leyes de la República del Ecuador.\n\nSitio web oficial: https://www.jamaec.com\nCorreo de soporte: soporte@jamaec.com\n\nJama es una plataforma digital que conecta locales comerciales (restaurantes, cafeterías, tiendas y otros establecimientos) con usuarios finales para la compra y entrega de productos.',
  },
  {
    title: '2. Objeto del servicio',
    body: 'La presente política regula la relación entre Jama y los locales afiliados que deseen ofrecer sus productos a través de la aplicación móvil y/o página web.\n\nJama actúa como:\n• Plataforma tecnológica intermediaria.\n• Facilitador de pagos digitales.\n• Canal de promoción y visibilidad comercial.\n• Sistema de gestión de pedidos.\n\nJama no es propietaria de los productos ofrecidos por el local, ni es responsable de su preparación, calidad o condiciones sanitarias.',
  },
  {
    title: '3. Registro del local',
    body: 'Para formar parte de la plataforma, el local deberá:\n• Proporcionar información veraz y actualizada.\n• Entregar RUC activo.\n• Proporcionar datos bancarios correctos.\n• Indicar dirección física exacta del establecimiento.\n• Listar productos con precios actualizados.\n• Cumplir la normativa sanitaria y comercial vigente en Ecuador.\n\nJama se reserva el derecho de aprobar o rechazar solicitudes de afiliación.',
  },
  {
    title: '4. Obligaciones del local',
    body: 'El local se compromete a:\n• Mantener actualizados precios y disponibilidad.\n• Garantizar la calidad y correcta preparación de los productos.\n• Cumplir tiempos de preparación razonables.\n• Empacar adecuadamente los pedidos.\n• Cumplir la normativa de higiene y permisos municipales.\n• No utilizar la plataforma para comercializar productos ilegales.\n\nEstá estrictamente prohibido vender drogas o sustancias ilícitas, armas, productos falsificados o cualquier artículo prohibido por la ley ecuatoriana.',
  },
  {
    title: '5. Comisiones y pagos',
    body: 'Jama cobrará una comisión por cada pedido completado, cuyo porcentaje será establecido en el contrato comercial individual con el local.\n\nLos pagos se realizarán mediante transferencia bancaria en los plazos acordados.\n\nJama podrá retener valores en caso de reclamos verificados, fraudes o cancelaciones indebidas atribuibles al local.',
  },
  {
    title: '6. Cancelaciones y reclamos',
    body: 'Si un pedido es cancelado por falta de stock, error en precios, incumplimiento de tiempos o negativa injustificada, Jama podrá aplicar penalizaciones internas.\n\nEl local es responsable directo ante el cliente por la calidad, presentación, cantidad y condiciones sanitarias de los productos.',
  },
  {
    title: '7. Uso de marca y contenido',
    body: 'El local autoriza a Jama a publicar su logo, utilizar imágenes de sus productos, realizar campañas promocionales e incluirlo en material publicitario digital.\n\nEl local declara tener derechos sobre las imágenes proporcionadas.',
  },
  {
    title: '8. Responsabilidad',
    body: 'Jama actúa exclusivamente como intermediario tecnológico. No es responsable por daños derivados del consumo, intoxicaciones alimentarias, incumplimientos sanitarios ni problemas internos del establecimiento.\n\nLa responsabilidad sobre los productos recae exclusivamente en el local.',
  },
  {
    title: '9. Terminación del servicio',
    body: 'Jama podrá suspender o cancelar la cuenta del local en caso de incumplimiento reiterado, mala reputación o múltiples reclamos, actividades ilegales o daño a la imagen de la plataforma.\n\nEl local podrá solicitar su retiro notificando por escrito.',
  },
  {
    title: '10. Protección de datos',
    body: 'El local se compromete a tratar los datos de clientes conforme a la normativa ecuatoriana de protección de datos. Está prohibido utilizar información de clientes para fines distintos a la gestión del pedido.',
  },
  {
    title: '11. Modificaciones',
    body: 'Jama podrá actualizar estos términos en cualquier momento. Las modificaciones serán notificadas por medios digitales.',
  },
  {
    title: '12. Jurisdicción',
    body: 'Estos términos se rigen por las leyes de la República del Ecuador. Cualquier disputa será resuelta ante los tribunales competentes del Ecuador.',
  },
];

// raw: numeric target, prefix/suffix: decorators, decimalPlaces for floats
const STATS = [
  { raw: 2400, prefix: '',  suffix: '+', decimalPlaces: 0, label: 'Pedidos al mes',     icon: '📦' },
  { raw: 1200, prefix: '$', suffix: '',  decimalPlaces: 0, label: 'Ingreso extra prom.', icon: '💵' },
  { raw: 4.8,  prefix: '',  suffix: '★', decimalPlaces: 1, label: 'Satisfacción',        icon: '⭐' },
  { raw: 98,   prefix: '',  suffix: '%', decimalPlaces: 0, label: 'Clientes satisfechos', icon: '😊' },
];

const FEAT_ICONS = {
  ventas: { bg: '#FFF7ED', color: '#FF8700', emoji: '\uD83D\uDCC8' },
  clientes: { bg: '#EFF6FF', color: '#3B82F6', emoji: '\uD83D\uDC65' },
  movil: { bg: '#F0FDF4', color: '#16A34A', emoji: '\uD83D\uDCF1' },
  rapido: { bg: '#FAF5FF', color: '#8B5CF6', emoji: '\u23F1\uFE0F' },
};

function FeatIcon({ type }) {
  const d = FEAT_ICONS[type];
  return (
    <div style={{
      width: 48, height: 48, borderRadius: 14,
      background: d.bg, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontSize: '1.4rem', flexShrink: 0,
    }}>
      {d.emoji}
    </div>
  );
}

const FEATURES = [
  {
    iconType: 'ventas',
    title: 'Incrementa tus ventas',
    desc: 'Negocios afiliados reportan hasta un 40% de ingresos extra en los primeros 90 dias.',
    metric: '+40%',
    metricLabel: 'ventas',
  },
  {
    iconType: 'clientes',
    title: 'Miles de nuevos clientes',
    desc: 'Apareces frente a usuarios hambrientos en tu zona, sin inversion en publicidad.',
    metric: '+3K',
    metricLabel: 'usuarios/mes',
  },
  {
    iconType: 'movil',
    title: 'Gestion 100% movil',
    desc: 'Pedidos, menu, ingresos y calificaciones en tiempo real desde tu celular.',
    metric: '100%',
    metricLabel: 'digital',
  },
  {
    iconType: 'rapido',
    title: 'Recibe pedidos hoy mismo',
    desc: 'Registro rapido: tu local puede empezar a operar en la plataforma el mismo dia.',
    metric: '<24h',
    metricLabel: 'activacion',
  },
];

const PROCESS_STEPS = [
  { num: '01', title: 'Registra tu negocio', desc: 'Completa el formulario con los datos de tu local en menos de 10 minutos.', color: '#FF8700' },
  { num: '02', title: 'Verificamos tu información', desc: 'Nuestro equipo revisa tus datos y activa tu cuenta en menos de 24 horas.', color: '#17c4b8' },
  { num: '03', title: 'Configura tu menú', desc: 'Sube tus productos, establece precios y define tus horarios de atención.', color: '#3B82F6' },
  { num: '04', title: 'Empieza a vender', desc: 'Recibe pedidos de clientes en tu zona y haz crecer tu negocio.', color: '#16A34A' },
];

const TESTIMONIALS = [
    {
        avatar: '👨‍🍳',
        name: 'Carlos M.',
        biz: 'Restaurante El Fogón',
        city: 'Ibarra',
        quote: 'Al inicio tenía dudas, pero desde el primer mes noté el cambio. Comenzaron a llegar pedidos nuevos todos los días y las ventas subieron bastante.',
        result: '+$1,150/mes',
    },
    {
        avatar: '👩‍💼',
        name: 'Andrea L.',
        biz: 'Cafetería Dulce Aroma',
        city: 'Ibarra',
        quote: 'En la primera semana ya estaba recibiendo pedidos constantes. Para una cafetería pequeña como la mía, eso hace la diferencia.',
        result: '60 pedidos/sem',
    },
    {
        avatar: '👨‍💼',
        name: 'Roberto P.',
        biz: 'Pizzería La Leña',
        city: 'Ibarra',
        quote: 'Antes dependía solo de la gente que pasaba por el local. Ahora tengo pedidos incluso en horas bajas. Es como tener otro punto de venta.',
        result: '+$1,200/mes',
    },
];

const DOCS = [
  { icon: '📄', label: 'RUC activo de tu negocio' },
  { icon: '🪪', label: 'Cédula del representante legal' },
  { icon: '🏦', label: 'Número de cuenta bancaria' },
  { icon: '📍', label: 'Dirección física exacta del local' },
  { icon: '🖼️', label: 'Logo o imagen de tu negocio' },
];

const PERKS = ['Registro gratuito', 'Soporte incluido', 'Sin mensualidades'];

/* ─── Count-up component ──────────────────────────────────── */
const CountUp = ({ to, prefix = '', suffix = '', decimalPlaces = 0, active, duration = 1800 }) => {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!active) return;
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = eased * to;
      setVal(decimalPlaces > 0
        ? Math.round(current * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces)
        : Math.round(current)
      );
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, to, duration, decimalPlaces]);

  const display = decimalPlaces > 0
    ? val.toFixed(decimalPlaces)
    : val.toLocaleString('en-US');

  return <span>{prefix}{display}{suffix}</span>;
};

/* ─── Auto-slide hook ─────────────────────────────────────── */
function useAutoSlide(count, ms = 4000) {
  const [idx, setIdx] = useState(0);
  const intervalRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(
      () => setIdx((a) => (a + 1) % count),
      ms,
    );
  }, [count, ms]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  const go = useCallback((i) => {
    setIdx(((i % count) + count) % count);
    startTimer();
  }, [count, startTimer]);

  return { idx, go, resetTimer: startTimer };
}

/* ─── Carousel dots ───────────────────────────────────────── */
const Dots = ({ count, active, onDot }) => (
  <div className="ro-carousel-dots">
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        className={`ro-carousel-dot${i === active ? ' ro-carousel-dot--on' : ''}`}
        onClick={() => onDot(i)}
        aria-label={`Ir a ${i + 1}`}
      />
    ))}
  </div>
);

/* ─── Docs modal ──────────────────────────────────────────── */
const DocsModal = ({ onClose, onConfirm }) => {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return createPortal(
    <div className="ro-modal-backdrop" onClick={onClose} role="dialog" aria-modal>
      <div className="ro-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ro-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>

        <div className="ro-modal-icon">📋</div>
        <h3 className="ro-modal-title">¿Qué necesitas para registrarte?</h3>
        <p className="ro-modal-sub">Ten estos documentos a la mano antes de comenzar.</p>

        <ul className="ro-modal-docs">
          {DOCS.map((d) => (
            <li key={d.label} className="ro-modal-doc">
              <span className="ro-modal-doc-ico">{d.icon}</span>
              <span className="ro-modal-doc-label">{d.label}</span>
            </li>
          ))}
        </ul>

        <div className="ro-modal-time">
          <span aria-hidden>⏱</span>
          El registro toma menos de 10 minutos
        </div>

        <button className="ro-welcome-btn ro-modal-cta" onClick={onConfirm}>
          Comenzar registro →
        </button>
      </div>
    </div>,
    document.body,
  );
};

/* ─── Main component ──────────────────────────────────────── */
const StepRestaurantWelcome = ({ onNext }) => {
  const [termsOpen, setTermsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const testSlide  = useAutoSlide(TESTIMONIALS.length, 5200);
  const testTrackRef = useRef(null);
  const statsRef     = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  /* Trigger count-up when stats scroll into view */
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* Sync testimonial track */
  useEffect(() => {
    const track = testTrackRef.current;
    if (!track) return;
    const child = track.children[testSlide.idx];
    if (child) track.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
  }, [testSlide.idx]);

  const handleTestScroll = useCallback(() => {
    const track = testTrackRef.current;
    if (!track) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== testSlide.idx) testSlide.go(i);
  }, [testSlide]);

  const openModal  = () => setShowModal(true);
  const closeModal = () => setShowModal(false);
  const confirm    = () => { closeModal(); onNext(); };

  return (
    <>
      {showModal && <DocsModal onClose={closeModal} onConfirm={confirm} />}

      <div className="ro-welcome">

        {/* ── Hero — Premium split layout ── */}
        <div className="ro-hero">
          <div className="ro-hero__left">
            <div className="ro-hero__content">
              <div className="ro-hero__badge">
                <span className="ro-hero__badge-dot" />
                Disponible en Ibarra
              </div>

              <h1 className="ro-hero__title">
                Haz crecer tu negocio con{' '}
                <span className="ro-hero__highlight">JAMA</span>
              </h1>

              <p className="ro-hero__sub">
                Conecta con miles de clientes hambrientos en tu zona.
                Recibe pedidos, aumenta tus ventas y gestiona todo desde tu celular.
              </p>

              <div className="ro-hero__stats-row">
                <div className="ro-hero__mini-stat">
                  <span className="ro-hero__mini-stat-val">+40%</span>
                  <span className="ro-hero__mini-stat-lbl">ventas extra</span>
                </div>
                <div className="ro-hero__mini-stat-sep" />
                <div className="ro-hero__mini-stat">
                  <span className="ro-hero__mini-stat-val">+3K</span>
                  <span className="ro-hero__mini-stat-lbl">clientes activos</span>
                </div>
                <div className="ro-hero__mini-stat-sep" />
                <div className="ro-hero__mini-stat">
                  <span className="ro-hero__mini-stat-val">&lt;10min</span>
                  <span className="ro-hero__mini-stat-lbl">registro</span>
                </div>
              </div>

              <div className="ro-hero__actions">
                <button className="ro-hero__cta" onClick={openModal}>
                  Registrar mi negocio gratis
                  <span className="ro-hero__cta-arrow">&#8594;</span>
                </button>
                <div className="ro-hero__trust">
                  <div className="ro-hero__trust-checks">
                    {PERKS.map((p) => (
                      <span key={p} className="ro-hero__check">&#10003; {p}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ro-hero__right">
            <div className="ro-hero__right-inner">
              <RestaurantStoryAnimation />
            </div>
          </div>
        </div>

        {/* ── Stats ribbon ── */}
        <div className="ro-wlc-stats-ribbon" ref={statsRef}>
          <div className="ro-wlc-stats-inner">
            {STATS.map((s, i) => (
              <div key={s.label} className="ro-wlc-stat-v2">
                <span className="ro-wlc-stat-v2__val">
                  <CountUp
                    to={s.raw}
                    prefix={s.prefix}
                    suffix={s.suffix}
                    decimalPlaces={s.decimalPlaces}
                    active={statsVisible}
                  />
                </span>
                <span className="ro-wlc-stat-v2__lbl">{s.label}</span>
                {i < STATS.length - 1 && <span className="ro-wlc-stat-v2__sep" />}
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="ro-wlc-body-v2">

          {/* ── Features grid ── */}
          <section className="ro-wlc-section">
            <div className="ro-wlc-section__header">
              <span className="ro-wlc-section__tag">Ventajas</span>
              <h2 className="ro-wlc-section__title">Todo lo que necesitas para vender más</h2>
              <p className="ro-wlc-section__sub">
                JAMA te conecta con clientes hambrientos en tu zona sin que inviertas un centavo en publicidad.
              </p>
            </div>
            <div className="ro-wlc-features">
              {FEATURES.map((f) => (
                <div key={f.title} className="ro-wlc-feat">
                  <div className="ro-wlc-feat__icon"><FeatIcon type={f.iconType} /></div>
                  <div className="ro-wlc-feat__body">
                    <h3 className="ro-wlc-feat__title">{f.title}</h3>
                    <p className="ro-wlc-feat__desc">{f.desc}</p>
                  </div>
                  <div className="ro-wlc-feat__metric">
                    <span className="ro-wlc-feat__metric-val">{f.metric}</span>
                    <span className="ro-wlc-feat__metric-lbl">{f.metricLabel}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── How it works ── */}
          <section className="ro-wlc-section ro-wlc-section--dark">
            <div className="ro-wlc-section__header">
              <span className="ro-wlc-section__tag ro-wlc-section__tag--light">Proceso</span>
              <h2 className="ro-wlc-section__title ro-wlc-section__title--light">Empieza en 4 simples pasos</h2>
            </div>
            <div className="ro-wlc-process">
              {PROCESS_STEPS.map((s, i) => (
                <div key={s.num} className="ro-wlc-proc-step">
                  <div className="ro-wlc-proc-step__num" style={{ background: s.color }}>{s.num}</div>
                  <h4 className="ro-wlc-proc-step__title">{s.title}</h4>
                  <p className="ro-wlc-proc-step__desc">{s.desc}</p>
                  {i < PROCESS_STEPS.length - 1 && <div className="ro-wlc-proc-step__connector" />}
                </div>
              ))}
            </div>
          </section>

          {/* ── Testimonials ── */}
          <section className="ro-wlc-section">
            <div className="ro-wlc-section__header">
              <span className="ro-wlc-section__tag">Testimonios</span>
              <h2 className="ro-wlc-section__title">Negocios reales, resultados reales</h2>
            </div>
            <div className="ro-wlc-testimonials">
              <div
                className="ro-wlc-test-track-v2"
                ref={testTrackRef}
                onScroll={handleTestScroll}
                onTouchStart={testSlide.resetTimer}
              >
                {TESTIMONIALS.map((t) => (
                  <div key={t.name} className="ro-wlc-test-v2">
                    <div className="ro-wlc-test-v2__top">
                      <div className="ro-wlc-test-v2__stars">{'★★★★★'}</div>
                      <span className="ro-wlc-test-v2__result">{t.result}</span>
                    </div>
                    <p className="ro-wlc-test-v2__quote">{t.quote}</p>
                    <div className="ro-wlc-test-v2__author">
                      <span className="ro-wlc-test-v2__avatar">{t.avatar}</span>
                      <div>
                        <p className="ro-wlc-test-v2__name">{t.name}</p>
                        <p className="ro-wlc-test-v2__biz">{t.biz} · {t.city}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Dots count={TESTIMONIALS.length} active={testSlide.idx} onDot={testSlide.go} />
            </div>
          </section>

          {/* ── CTA banner ── */}
          <section className="ro-wlc-cta-banner">
            <div className="ro-wlc-cta-banner__content">
              <h2 className="ro-wlc-cta-banner__title">
                Tu competencia ya está en JAMA
              </h2>
              <p className="ro-wlc-cta-banner__sub">
                Cada día sin JAMA son pedidos que van a otro local. Regístrate gratis hoy.
              </p>
              <button className="ro-wlc-cta-banner__btn" onClick={openModal}>
                Registrar mi negocio
                <span>→</span>
              </button>
            </div>
          </section>

          {/* ── Terms accordion ── */}
          <div className="ro-wlc-terms-v2">
            <button
              type="button"
              className="ro-wlc-terms-v2__toggle"
              onClick={() => setTermsOpen((o) => !o)}
              aria-expanded={termsOpen}
            >
              <span className="ro-wlc-terms-v2__toggle-text">Términos y condiciones para locales afiliados</span>
              <span className={`ro-wlc-terms-v2__arrow${termsOpen ? ' ro-wlc-terms-v2__arrow--open' : ''}`}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </button>
            {termsOpen && (
              <div className="ro-wlc-terms-v2__body">
                <p className="ro-wlc-terms-v2__meta">
                  JAMA – Beez Cía. Ltda. · Última actualización: febrero 2026
                </p>
                {TERMS_SECTIONS.map((s) => (
                  <div key={s.title} className="ro-wlc-terms-v2__section">
                    <h4 className="ro-wlc-terms-v2__heading">{s.title}</h4>
                    {s.body.split('\n').map((line, i) =>
                      line.trim() === '' ? null : (
                        <p key={i} className="ro-wlc-terms-v2__p">{line}</p>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default StepRestaurantWelcome;
